"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo } from "react";

import type { BrowseState } from "@/lib/movies/browse-params";
import type { LanguageCode } from "@/config/i18n";
import type { Movie } from "@/types/movie";
import {
  parseBrowseParams,
  serializeBrowseParams,
} from "@/lib/movies/browse-params";
import { filterMovies, sortMovies } from "@/lib/movies/service";

import { MoviesView, PENDING_ATTRIBUTE } from "./MoviesView";

interface MoviesClientProps {
  lang: LanguageCode;
  movies: Movie[];
  availableGenres: string[];
  yearRange: { min: number; max: number };
}

const SCROLL_STORAGE_KEY = "moviesScrollPosition";

/** Remember the scroll position when opening a movie, restore it on return. */
function useScrollRestoration() {
  useEffect(() => {
    const browserHistory = window.history;
    const previous = browserHistory.scrollRestoration;

    browserHistory.scrollRestoration = "manual";

    let frame = 0;
    const saved = sessionStorage.getItem(SCROLL_STORAGE_KEY);

    if (saved) {
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          window.scrollTo({ top: Number(saved) || 0, behavior: "instant" });
        });
      });
    }

    const handleClick = (event: MouseEvent) => {
      const link = (event.target as HTMLElement | null)?.closest("a");
      const href = link?.getAttribute("href");

      if (href && /^\/[a-z]{2}\/movies\/[^/?#]+$/.test(href)) {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("click", handleClick);
      browserHistory.scrollRestoration = previous;
    };
  }, []);
}

export function MoviesClient({
  lang,
  movies,
  availableGenres,
  yearRange,
}: MoviesClientProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const query = searchParams.toString();

  // The URL is the single source of truth for search, filters and sort.
  const state = useMemo(() => {
    const parsed = parseBrowseParams(new URLSearchParams(query));
    const genres = parsed.filters.genres?.filter((genre) =>
      availableGenres.includes(genre),
    );

    return {
      ...parsed,
      filters: {
        ...parsed.filters,
        genres: genres && genres.length > 0 ? genres : undefined,
      },
    };
  }, [query, availableGenres]);

  /** Update the URL in place (no navigation, no server round trip). */
  const updateState = useCallback(
    (change: (current: BrowseState) => BrowseState) => {
      // Read the live URL so debounced updates never use a stale state.
      const current = parseBrowseParams(
        new URLSearchParams(window.location.search),
      );
      const next = serializeBrowseParams(change(current));

      window.history.replaceState(
        null,
        "",
        next ? `${pathname}?${next}` : pathname,
      );
    },
    [pathname],
  );

  useScrollRestoration();

  // The real results are rendered now; release the static fallback's guard.
  useEffect(() => {
    document.documentElement.removeAttribute(PENDING_ATTRIBUTE);
  }, []);

  const results = useMemo(
    () =>
      sortMovies(
        filterMovies(movies, state.filters),
        state.sort,
        state.filters.search,
      ),
    [movies, state],
  );

  return (
    <MoviesView
      availableGenres={availableGenres}
      filters={state.filters}
      lang={lang}
      movies={results}
      sortOptions={state.sort}
      totalCount={movies.length}
      yearRange={yearRange}
      onFiltersChange={(filters) =>
        updateState((current) => ({
          ...current,
          filters: { ...filters, search: current.filters.search },
        }))
      }
      onSearchChange={(search) =>
        updateState((current) => ({
          ...current,
          filters: { ...current.filters, search },
        }))
      }
      onSortChange={(sort) => updateState((current) => ({ ...current, sort }))}
    />
  );
}
